import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import useSeoMeta from '../hooks/useSeoMeta';
import { confirmPasswordReset } from '../api/passwordReset';
import { extractApiErrorMessage } from '../utils/apiError';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useSeoMeta(
    'Reset Password — Pinecurl',
    'Set a new password for your Pinecurl account.'
  );

  // Redirect to forgot-password if no token is present
  useEffect(() => {
    if (!token) {
      navigate('/password-reset', { replace: true });
    }
  }, [token, navigate]);

  const validate = (): boolean => {
    const errors: { newPassword?: string; confirmPassword?: string } = {};

    if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters.';
    } else if (!/[A-Z]/.test(newPassword)) {
      errors.newPassword = 'Must include at least one uppercase letter.';
    } else if (!/[a-z]/.test(newPassword)) {
      errors.newPassword = 'Must include at least one lowercase letter.';
    } else if (!/[0-9]/.test(newPassword)) {
      errors.newPassword = 'Must include at least one number.';
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    setIsLoading(true);
    try {
      await confirmPasswordReset({ token, newPassword });
      setIsSuccess(true);
      // Auto-redirect to login after 3 seconds
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link
            to="/login"
            className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
          >
            ← Back to sign in
          </Link>
        </div>

        <div className="bg-surface border border-border rounded-xl p-8 shadow-lg">
          {isSuccess ? (
            /* ── Success state ── */
            <div className="text-center">
              <div
                className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-4"
                aria-hidden="true"
              >
                <svg
                  className="w-7 h-7 text-emerald-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-text-primary mb-2">Password updated!</h1>
              <p className="text-text-secondary text-sm leading-relaxed mb-6">
                Your password has been changed successfully. Redirecting you to sign in…
              </p>
              <Link
                to="/login"
                className="btn btn--primary w-full inline-block text-center"
              >
                Sign in now
              </Link>
            </div>
          ) : (
            /* ── Reset form ── */
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-text-primary">Set new password</h1>
                <p className="text-text-secondary text-sm mt-2">
                  Choose a strong password for your account.
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm">
                  {error}{' '}
                  {(error.toLowerCase().includes('expired') ||
                    error.toLowerCase().includes('invalid') ||
                    error.toLowerCase().includes('used')) && (
                    <Link
                      to="/password-reset"
                      className="underline text-accent-red hover:text-red-300 ml-1"
                    >
                      Request a new reset link.
                    </Link>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <Input
                  label="New password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, newPassword: undefined }));
                  }}
                  placeholder="Min. 8 chars, upper, lower, number"
                  required
                  autoFocus
                  error={fieldErrors.newPassword}
                />
                <Input
                  label="Confirm new password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                  }}
                  placeholder="Re-enter your new password"
                  required
                  error={fieldErrors.confirmPassword}
                />

                <Button type="submit" className="w-full mt-2" isLoading={isLoading}>
                  Update password
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
